import { create } from 'zustand';
import { toast } from 'react-toastify';
import { getEngineers } from '../../../api/api';
import {
  getSupportMessages,
  getSupportSummary,
  getSupportThread,
  getSupportThreads,
  markSupportThreadRead,
  patchSupportThread,
  sendSupportMessage,
  uploadSupportAttachment,
} from '../api';
import { MESSAGE_PAGE_SIZE, THREAD_PAGE_SIZE } from '../constants';
import { createIdempotencyKey } from '../utils';

let threadsRequestId = 0;
let messagesRequestId = 0;

const threadTime = (item) => new Date(item?.updated_at || item?.last_message?.created_at || 0).getTime();

const resolveAssignee = (assignee, engineerId) => {
  if (!assignee || assignee === 'all') return undefined;
  if (assignee === 'mine') return engineerId || undefined;
  return assignee;
};

const mergeMessages = (current, incoming) => {
  const map = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => map.set(item.id, item));
  return [...map.values()].sort((a, b) => a.id - b.id);
};

const applyThreadToList = (item, thread) => {
  if (!thread) return item;
  return {
    ...item,
    ...thread,
    user: { ...item.user, ...thread.user },
    last_message: item.last_message,
  };
};

export const useSupportStore = create((set, get) => {
  const errorFallback = (status) => {
    if (status === 429) return 'Слишком часто, попробуйте через минуту';
    if (status === 413) return 'Файл превышает лимит';
    if (status === 500 || status === 503) return 'Сервис временно недоступен';
    return '';
  };

  const reportError = (err, { quiet = false, dropThread = false, reload = true } = {}) => {
    if (!err || quiet || err.status === 401) return;

    if (err.status >= 500 && (err.type || err.details)) {
      console.error(err.type, err.details);
    }

    const message = err.error || errorFallback(err.status);
    if (message) toast.error(message);

    if (dropThread && err.status === 404) {
      const threadId = err.threadId || get().selectedId;
      set({
        selectedId: null,
        thread: null,
        messages: [],
        threadLoading: false,
        messagesLoading: false,
        threadError: { ...err, threadId },
      });
      get().fetchThreads({ silent: true });
    }

    if (reload && err.status === 409) get().reloadThread();
  };

  return {
    summary: { new: 0, in_progress: 0, unread_messages: 0 },

    threads: [],
    threadsTotal: 0,
    threadsLoading: false,
    threadsLoaded: false,
    threadsError: null,
    filters: {
      status: '',
      organizationId: '',
      assignee: 'all',
      unreadOnly: false,
      q: '',
    },
    page: 0,
    listUpdatedAt: null,
    engineerId: null,
    engineers: [],

    selectedId: null,
    thread: null,
    threadLoading: false,
    threadError: null,
    updatingStatus: false,

    messages: [],
    messagesTotal: 0,
    messagesLoading: false,
    messagesError: null,
    loadingOlder: false,
    hasMoreMessages: false,
    scrollToken: 0,

    sending: false,
    uploadProgress: null,

    setEngineerId: (engineerId) => set({ engineerId }),

    setFilters: (patch) => {
      set((state) => ({ filters: { ...state.filters, ...patch }, page: 0 }));
    },

    setPage: (page) => set({ page }),

    fetchSummary: async () => {
      const data = await getSupportSummary({ errCb: () => {} });
      if (!data) return;
      set({
        summary: {
          new: data.new ?? 0,
          in_progress: data.in_progress ?? 0,
          unread_messages: data.unread_messages ?? 0,
        },
      });
    },

    loadEngineers: async () => {
      if (get().engineers.length) return;

      await getEngineers(
        1,
        200,
        null,
        (data) => {
          set({ engineers: data?.items ?? [] });
        },
        () => {}
      );
    },

    fetchThreads: async ({ silent } = {}) => {
      const requestId = ++threadsRequestId;
      const startedAt = new Date().toISOString();
      const { filters, page, engineerId } = get();

      if (!silent) set({ threadsLoading: true, threadsError: null });

      let error = null;
      const data = await getSupportThreads({
        status: filters.status,
        organizationId: filters.organizationId,
        assigneeId: resolveAssignee(filters.assignee, engineerId),
        unreadOnly: filters.unreadOnly,
        q: filters.q,
        page,
        count: THREAD_PAGE_SIZE,
        errCb: (err) => {
          error = err;
        },
      });

      if (requestId !== threadsRequestId) return;

      if (!data) {
        set({ threadsLoading: false, threadsLoaded: true, threadsError: error });
        return;
      }

      set({
        threads: data.items ?? [],
        threadsTotal: data.count ?? 0,
        threadsLoading: false,
        threadsLoaded: true,
        threadsError: null,
        listUpdatedAt: startedAt,
      });
    },

    pollThreads: async () => {
      const requestId = threadsRequestId;
      const startedAt = new Date().toISOString();
      const { listUpdatedAt, filters, engineerId } = get();
      if (!listUpdatedAt) return;

      const data = await getSupportThreads({
        status: filters.status,
        organizationId: filters.organizationId,
        assigneeId: resolveAssignee(filters.assignee, engineerId),
        unreadOnly: filters.unreadOnly,
        q: filters.q,
        updatedAfter: listUpdatedAt,
        page: 0,
        count: 50,
        errCb: () => {},
      });

      if (!data || requestId !== threadsRequestId) return;

      const items = data.items ?? [];
      let readId = null;

      set((state) => {
        const map = new Map(state.threads.map((item) => [item.id, item]));
        const currentIds = new Set(state.threads.map((item) => item.id));

        items.forEach((item) => {
          if (state.page === 0 || currentIds.has(item.id)) {
            map.set(item.id, { ...map.get(item.id), ...item });
          }
        });

        let threads = [...map.values()].sort((a, b) => threadTime(b) - threadTime(a));
        if (state.page === 0) threads = threads.slice(0, THREAD_PAGE_SIZE);

        const updated = items.find((item) => item.id === state.selectedId);
        let thread = state.thread;
        if (updated && thread && thread.id === updated.id) {
          thread = {
            ...thread,
            status: updated.status,
            assignee: updated.assignee,
            organization: updated.organization ?? thread.organization,
            unread_count: updated.unread_count,
            user: { ...thread.user, ...updated.user },
          };
          if (updated.unread_count > 0) readId = updated.id;
        }

        return { threads, thread, listUpdatedAt: startedAt };
      });

      if (readId) get().markRead(readId);
    },

    reloadThread: async () => {
      const threadId = get().selectedId;
      if (!threadId) return;

      let error = null;
      const thread = await getSupportThread({
        threadId,
        errCb: (err) => {
          error = err;
        },
      });

      if (!thread) {
        reportError(error ? { ...error, threadId } : error, { dropThread: true, reload: false });
        return;
      }

      set((state) => ({
        thread,
        threads: state.threads.map((item) => (item.id === thread.id ? applyThreadToList(item, thread) : item)),
      }));
    },

    openThread: async (id) => {
      if (!id) {
        ++messagesRequestId;
        set({
          selectedId: null,
          thread: null,
          messages: [],
          messagesTotal: 0,
          hasMoreMessages: false,
          threadLoading: false,
          messagesLoading: false,
          messagesError: null,
          threadError: null,
        });
        return;
      }

      const requestId = ++messagesRequestId;
      set({
        selectedId: id,
        thread: null,
        messages: [],
        messagesTotal: 0,
        hasMoreMessages: false,
        threadLoading: true,
        messagesLoading: true,
        messagesError: null,
        threadError: null,
      });

      let error = null;
      const thread = await getSupportThread({
        threadId: id,
        errCb: (err) => {
          error = err;
        },
      });

      if (requestId !== messagesRequestId) return;

      if (!thread) {
        const nextError = error ? { ...error, threadId: id } : null;
        set({ threadLoading: false, messagesLoading: false, threadError: nextError, selectedId: error?.status === 404 ? null : id });
        reportError(nextError, { dropThread: error?.status === 404 });
        return;
      }

      const page = await getSupportMessages({
        threadId: id,
        count: MESSAGE_PAGE_SIZE,
        errCb: (err) => {
          error = err;
        },
      });

      if (requestId !== messagesRequestId) return;

      if (!page) {
        if (error?.status === 404) {
          set({ threadLoading: false, messagesLoading: false });
          reportError({ ...error, threadId: id }, { dropThread: true });
          return;
        }

        set({ thread, threadLoading: false, messagesLoading: false, messagesError: error });
        reportError(error);
        return;
      }

      const messages = page.items ?? [];
      set((state) => ({
        thread,
        threadLoading: false,
        messages,
        messagesTotal: page.count ?? messages.length,
        hasMoreMessages: messages.length < (page.count ?? messages.length),
        messagesLoading: false,
        messagesError: null,
        threads: state.threads.map((item) => (item.id === thread.id ? applyThreadToList(item, thread) : item)),
      }));

      get().markRead(id);
    },

    loadOlderMessages: async () => {
      const { selectedId, messages, hasMoreMessages, loadingOlder } = get();
      const oldest = messages[0];
      if (!selectedId || !oldest || !hasMoreMessages || loadingOlder) return false;

      const requestId = messagesRequestId;
      set({ loadingOlder: true });

      const page = await getSupportMessages({
        threadId: selectedId,
        beforeId: oldest.id,
        count: MESSAGE_PAGE_SIZE,
        errCb: () => {},
      });

      if (requestId !== messagesRequestId) {
        set({ loadingOlder: false });
        return false;
      }

      const items = page?.items ?? [];
      const known = new Set(get().messages.map((item) => item.id));
      const added = items.some((item) => !known.has(item.id));

      if (!page || !added) {
        set({ loadingOlder: false, hasMoreMessages: page ? false : get().hasMoreMessages });
        return false;
      }

      set((state) => {
        const next = mergeMessages(state.messages, items);
        const total = page.count ?? state.messagesTotal;
        return {
          messages: next,
          messagesTotal: total,
          hasMoreMessages: next.length < total,
          loadingOlder: false,
        };
      });

      return true;
    },

    pollMessages: async () => {
      const { selectedId, messages } = get();
      const threadId = selectedId;
      if (!threadId) return;

      const requestId = messagesRequestId;
      const newest = messages[messages.length - 1];
      const page = await getSupportMessages({
        threadId,
        afterId: newest?.id,
        count: MESSAGE_PAGE_SIZE,
        errCb: () => {},
      });

      if (!page || requestId !== messagesRequestId || get().selectedId !== threadId) return;

      const items = page.items ?? [];
      if (!items.length) return;

      set((state) => ({
        messages: mergeMessages(state.messages, items),
        messagesTotal: page.count ?? state.messagesTotal,
      }));

      get().markRead(threadId);
    },

    refreshAttachmentUrls: async () => {
      const { selectedId, messages } = get();
      const newest = messages[messages.length - 1];
      if (!selectedId || !newest) return;
      if (!messages.some((item) => item.attachments?.length)) return;

      const requestId = messagesRequestId;
      const page = await getSupportMessages({
        threadId: selectedId,
        beforeId: newest.id + 1,
        count: Math.min(Math.max(messages.length, MESSAGE_PAGE_SIZE), 200),
        errCb: () => {},
      });

      if (!page?.items?.length || requestId !== messagesRequestId || get().selectedId !== selectedId) return;

      set((state) => ({
        messages: mergeMessages(state.messages, page.items),
      }));
    },

    markRead: async (threadId) => {
      const id = threadId || get().selectedId;
      if (!id) return;

      const unread = get().threads.find((item) => item.id === id)?.unread_count || get().thread?.unread_count || 0;
      const result = await markSupportThreadRead({ threadId: id, errCb: () => {} });
      if (result === null || get().selectedId !== id) return;

      set((state) => ({
        threads: state.threads.map((item) => (item.id === id ? { ...item, unread_count: 0 } : item)),
        thread: state.thread?.id === id ? { ...state.thread, unread_count: 0 } : state.thread,
        summary: {
          ...state.summary,
          unread_messages: Math.max(0, (state.summary.unread_messages || 0) - unread),
        },
      }));
    },

    changeStatus: async (status) => {
      const threadId = get().selectedId;
      if (!threadId) return;

      set({ updatingStatus: true });
      let error = null;
      const thread = await patchSupportThread({
        threadId,
        body: { status },
        errCb: (err) => {
          error = err;
        },
      });
      set({ updatingStatus: false });

      if (!thread) {
        reportError(error, { dropThread: true });
        return;
      }

      set((state) => ({
        thread,
        threads: state.threads.map((item) => (item.id === thread.id ? applyThreadToList(item, thread) : item)),
      }));
    },

    sendMessage: async ({ text, file, attachmentId, idempotencyKey }) => {
      const threadId = get().selectedId;
      const trimmed = text?.trim() ?? '';
      if (!threadId || (!trimmed && !file && !attachmentId)) return { ok: false };

      const requestKey = idempotencyKey || createIdempotencyKey();

      set({ sending: true, uploadProgress: file && !attachmentId ? 0 : null });

      let nextAttachmentId = attachmentId ?? null;
      if (file && !nextAttachmentId) {
        let uploadError = null;
        const uploaded = await uploadSupportAttachment({
          file,
          onProgress: (value) => set({ uploadProgress: value }),
          errCb: (err) => {
            uploadError = err;
          },
        });

        if (!uploaded?.attachment_id) {
          set({ sending: false, uploadProgress: null });
          reportError(uploadError);
          return { ok: false };
        }

        nextAttachmentId = uploaded.attachment_id;
      }

      let sendError = null;
      const message = await sendSupportMessage({
        threadId,
        text: trimmed,
        attachmentId: nextAttachmentId,
        idempotencyKey: requestKey,
        errCb: (err) => {
          sendError = err;
        },
      });

      set({ sending: false, uploadProgress: null });

      if (!message?.id) {
        reportError(sendError, { dropThread: true });
        return { ok: false, attachmentId: nextAttachmentId, idempotencyKey: requestKey };
      }

      if (get().selectedId !== threadId) return { ok: true };

      set((state) => ({
        messages: mergeMessages(state.messages, [message]),
        messagesTotal: state.messages.some((item) => item.id === message.id) ? state.messagesTotal : state.messagesTotal + 1,
        scrollToken: state.scrollToken + 1,
      }));

      return { ok: true };
    },
  };
});
